import { LoadingButton } from '@mui/lab';
import { Box, Divider, FormControl, FormHelperText, FormLabel, Grid, MenuItem, Select, TextField, Typography, useTheme } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import Modal from '../../components/Modal';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import CreditCardInput from 'react-credit-card-input';
import creditCardType from 'credit-card-type';
import payment from 'payment';
import isExpiryInvalid from '../../utils/isExpiryInvalid';

function replaceWithCase(word, replacement) {
  if (word === word.toUpperCase()) {
    return replacement.toUpperCase(); // EXPIRY → EXPIRATION
  }
  if (word === word.toLowerCase()) {
    return replacement.toLowerCase(); // expiry → expiration
  }
  if (word[0] === word[0].toUpperCase() && word.slice(1) === word.slice(1).toLowerCase()) {
    return replacement[0].toUpperCase() + replacement.slice(1).toLowerCase(); // Expiry → Expiration
  }

  // Fallback: preserve letter casing per character (eXpIrY → eXpIrAtIoN)
  return replacement
    .split("")
    .map((ch, i) =>
      word[i] && word[i] === word[i].toUpperCase()
        ? ch.toUpperCase()
        : ch.toLowerCase()
    )
    .join("");
}

const validationSchema = Yup.object({
    nameOnCard: Yup.string().required('Name on card is required'),
    country: Yup.string().required('Country is required'),
    city: Yup.string().required('City is required'),
    address1: Yup.string().required('Address line 1 is required'),
    state: Yup.string().required('State is required'),
    postalCode: Yup.string()
        .required('Postal code is required')
        .matches(/^\d{5}$/, 'Must be a 5-digit number'),
});

const US_STATES = [
    { code: 'AL', name: 'Alabama' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DE', name: 'Delaware' },
    { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' },
];

function AddNewPayment({ onPaymentDetails }) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const { values, touched, errors, getFieldProps, handleSubmit, setTouched, validateForm, resetForm } = useFormik({
        initialValues: {
            cardNickName: '',
            nameOnCard: '',
            creditCardNumber: '',
            cardExpirationMonth: '',
            cardExpirationYear: '',
            cvv: '',
            country: 'US', // directly added here
            address1: '',
            address2: '',
            city: '',
            state: '',
            postalCode: '',
        },
        validationSchema,
        onSubmit: (values) => {
            try {
                let cardType = creditCardType(cardNumber.substring(0, 4));
                let data = {
                    ...values,
                    cardType: cardType[0].niceType,
                    creditCardNumber: cardNumber,
                    cardExpirationMonth: expiry.split('/')[0].trim(),
                    cardExpirationYear: getFullYear(expiry.split('/')[1].trim()),
                    cvv: cvc,
                };
                setLoading(true);
                onPaymentDetails(data);
            } catch (error) {
                console.log(error, 'onSubmit error');
            }
        },
    });

    const [cardNumber, setCardNumber] = useState('');
    const [expiry, setExpiry] = useState('');
    const [cvc, setCvc] = useState('');

    const [cardError, setCardError] = useState('');
    const [cardInvalidError, setCardInvalidError] = useState('');

    const cardInputRef = useRef();
    cardInputRef.current = '';

    const theme = useTheme();

    useEffect(() => {
        if (cardInputRef.current) {
            cardInputRef.current.focus();
        }
    }, [cardInputRef.current]);

    const handleModalClose = () => {
        setIsModalOpen(false);
    };
    const handleModalOnSave = () => {
        //handleSubmit();
    };

    const handleModalOpen = () => {
        setLoading(false)
        setIsModalOpen(true);
        resetForm();

        setCardNumber();
        setExpiry();
        setCvc();

        setCardError('');
        setCardInvalidError('');
    }; 

    useEffect(() => {
        // Add the event listener
        window.addEventListener('ADD_PAYMENT_MODAL_OPEN', handleModalOpen);
        window.addEventListener('ADD_PAYMENT_MODAL_CLOSE', handleModalClose);
        window.addEventListener('ADD_PAYMENT_MODAL_STOP_LOADING', handleStopLoading);

        // Cleanup function to remove the event listener
        return () => {
            window.removeEventListener('ADD_PAYMENT_MODAL_OPEN', handleModalOpen);
            window.removeEventListener('ADD_PAYMENT_MODAL_CLOSE', handleModalClose);
            window.removeEventListener('ADD_PAYMENT_MODAL_STOP_LOADING', handleStopLoading);
        };
    }, []); // Empty dependency array ensures this runs only once when the component mounts

    const getFieldError = (name) => touched[name] && errors[name];

    const handleStopLoading = () => {
        setLoading(false);
    };

    const getFullYear = (input) => {
        // Automatically generate '20' as the prefix
        const currentYearPrefix = new Date().getFullYear().toString().slice(0, 2); // Gets '20'

        // Combine the prefix with the input
        const year = parseInt(currentYearPrefix + input);

        // Create a Date object for January 1st of that year
        const date = new Date(year, 0, 1);

        return date.getFullYear().toString();
    };

    console.log({
        cardError,
        cardInvalidError,
    });

    return (
        <>
            {/* Add Payment Method */}
            <Modal
                modalOpen={isModalOpen}
                title="Add payment method"
                hideActions={false}
                handleModalClose={handleModalClose}
                SubmitBtnUI={
                    <LoadingButton loading={loading} type="submit" variant="contained" onClick={handleModalOnSave}>
                        Save
                    </LoadingButton>
                }
                PaperProps={{
                    component: 'form',
                    sx: {
                        maxWidth: 650,

                        '& .creditCardNumberInput.input.is-card-empyty': {
                            border: '1px solid #FF5630',
                        },

                        '& .MuiInputBase-input.MuiOutlinedInput-input': {
                            padding: '10px 14px',
                        },

                        '& .MuiFormLabel-root': {
                            marginBottom: '0.2rem',
                        },

                        '@media (max-width:425px)': {
                            margin: '1rem',
                        },
                        '& .input': {
                            border: '1px solid gainsboro',
                            borderRadius: '10px',
                        },
                    },
                    onSubmit: async (e) => {
                        e.preventDefault();

                        // Mark all fields as touched
                        setTouched({
                            cardNickName: true,
                            nameOnCard: true,
                            creditCardNumber: true,
                            cardExpirationMonth: true,
                            cardExpirationYear: true,
                            cvv: true,
                            country: true,
                            address1: true,
                            address2: true,
                            city: true,
                            state: true,
                            postalCode: true,
                        });

                        // Validate the form
                        try {
                            await validateForm(); // Run validation

                            if (!(cardNumber && expiry && cvc)) {
                                setCardError('Credit card is required');
                            } else if (cardInvalidError) {
                                setCardError('Please enter valid card details');
                            } else if (!payment.fns.validateCardNumber(cardNumber)) {
                                setCardError('Please enter valid card Number');
                            } else if (isExpiryInvalid(expiry.split(' / ').join('/'))) {
                                const expiryDateInvalid = isExpiryInvalid(expiry.split(' / ').join('/'));
                                setCardError(expiryDateInvalid || 'Please enter valid Expiration Date');
                            } else if (!payment.fns.validateCardCVC(cvc)) {
                                setCardError('Please enter valid CVC');
                            } else if (Object.keys(errors).length === 0) {
                                handleSubmit(e); // If no errors, call Formik's submit handler
                            }
                        } catch (error) {
                            console.error('Validation error:', error);
                        }
                    },
                }}
            >
                <Box>
                    <Typography
                        component={'h2'}
                        color={'text.primary'}
                        textAlign={'start'}
                        fontSize={'customFontSize.medium'}
                        fontWeight={'fontWeightSemiBold'}
                        lineHeight={'normal'}
                        marginBottom={'0.8rem'}
                    >
                        Card Details
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item sm={12} xs={12}>
                            <FormControl fullWidth error={!!getFieldError('nameOnCard')}>
                                <FormLabel htmlFor="nameOnCard">Name on card *</FormLabel>
                                <TextField
                                    id="nameOnCard"
                                    type="text"
                                    name="nameOnCard"
                                    placeholder="Enter name"
                                    fullWidth
                                    variant="outlined"
                                    InputProps={{
                                        color: 'secondary',
                                    }}
                                    sx={{ '& .MuiInputBase-input': { fontSize: 'small' } }}
                                    {...getFieldProps('nameOnCard')}
                                    error={getFieldError('nameOnCard')}
                                    helperText={getFieldError('nameOnCard')}
                                />
                            </FormControl>
                        </Grid>

                        <Grid item sm={12} xs={12}>
                            <FormControl fullWidth error={cardError || cardInvalidError}>
                                <FormLabel htmlFor="creditCardNumber">Credit card *</FormLabel>
                                <CreditCardInput
                                    id="creditCardNumber"
                                    cardNumberInputProps={{
                                        value: cardNumber,
                                        onChange: (event) => {
                                            setCardNumber(event.target.value);

                                            //
                                            cardInvalidError && setCardInvalidError('');
                                            cardError && setCardError('');
                                        },
                                        onBlur: (e) => {
                                            // console.log(`${e.name} is blur creditCardNumber`);
                                        },
                                        onError: (e) => {
                                            setCardInvalidError(e);
                                            console.log(`${e} cardNumberInputProps`);
                                        },
                                    }}
                                    cardExpiryInputProps={{
                                        value: expiry,
                                        onChange: (event) => {
                                            setExpiry(event.target.value);

                                            //
                                            cardInvalidError && setCardInvalidError('');
                                            cardError && setCardError('');
                                        },
                                        onBlur: (e) => {
                                            // console.log(`${e} is blur expiry`);
                                        },
                                        onError: (e) => {
                                            let errorMsg = e;

                                            if (typeof e === 'string' && /expiry/i.test(e)) {
                                                errorMsg = e.replace(/expiry/gi, (match) => replaceWithCase(match, 'expiration'));
                                            }
                                            console.log(`${e} cardExpiryInputProps`);
                                            setCardInvalidError(errorMsg);
                                        },
                                    }}
                                    cardCVCInputProps={{
                                        value: cvc,
                                        onChange: (event) => {
                                            setCvc(event.target.value);

                                            //
                                            cardInvalidError && setCardInvalidError('');
                                            cardError && setCardError('');
                                        },
                                        onBlur: (e) => {
                                            // console.log(`${e} is blur cvc`);
                                        },
                                        onError: (e) => {
                                            setCardInvalidError(e);
                                            console.log(`${e} cardCVCInputProps`);
                                        },
                                    }}
                                    // onError={({ inputName, error }) => console.log(`credit card ${inputName} error: ${error}`)}
                                    fieldClassName={`creditCardNumberInput input ${cardError && 'is-card-empyty'}`}
                                    dangerTextStyle={{
                                        color: theme.palette.error.main,

                                        fontSize: '0.75rem',
                                        fontWeight: 400,
                                        lineHeight: 1.66,
                                        marginTop: '3px',
                                        marginRight: '14px',
                                        marginBottom: 0,
                                        marginLeft: '14px',

                                        display: 'none',
                                    }}
                                    invalidStyle={{
                                        border: `1px solid ${theme.palette.error.main}`,
                                    }}
                                />

                                {!cardInvalidError && cardError && <FormHelperText error>{cardError}</FormHelperText>}
                                {cardInvalidError && !cardError && <FormHelperText error>{cardInvalidError}</FormHelperText>}
                                {cardInvalidError && cardError && <FormHelperText error>{cardError}</FormHelperText>}
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ margin: '1rem 0' }} />

                <Box>
                    <Typography
                        component={'h2'}
                        color={'text.primary'}
                        textAlign={'start'}
                        fontSize={'customFontSize.medium'}
                        fontWeight={'fontWeightSemiBold'}
                        lineHeight={'normal'}
                        marginBottom={'0.8rem'}
                    >
                        Billing Address
                    </Typography>

                    <Grid container spacing={2}>
                        {/* <Grid item sm={6} xs={12}>
                            <FormControl fullWidth error={!!getFieldError('country')}>
                                <FormLabel htmlFor="country">Country*</FormLabel>
                                <TextField
                                    id="Country"
                                    type="text"
                                    name="country"
                                    placeholder="Enter country"
                                    fullWidth
                                    variant="outlined"
                                    InputProps={{
                                        color: 'secondary',
                                    }}
                                    sx={{ '& .MuiInputBase-input': { fontSize: 'small' } }}
                                    {...getFieldProps('country')}
                                    error={getFieldError('country')}
                                    helperText={getFieldError('country')}
                                />
                            </FormControl>
                        </Grid> */}

                        {/* <Grid
                            item
                            sm={6}
                            xs={12}
                            display={{
                                xs: 'none',
                                sm: 'grid',
                            }}
                        ></Grid> */}

                        <Grid item sm={6} xs={12}>
                            <FormControl fullWidth error={!!getFieldError('address1')}>
                                <FormLabel htmlFor="address1">Address line 1 *</FormLabel>
                                <TextField
                                    id="address1"
                                    type="text"
                                    name="address1"
                                    placeholder="Enter address line 1"
                                    fullWidth
                                    variant="outlined"
                                    InputProps={{
                                        color: 'secondary',
                                    }}
                                    sx={{ '& .MuiInputBase-input': { fontSize: 'small' } }}
                                    {...getFieldProps('address1')}
                                    error={getFieldError('address1')}
                                    helperText={getFieldError('address1')}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item sm={6} xs={12}>
                            <FormControl fullWidth error={!!getFieldError('address2')}>
                                <FormLabel htmlFor="address2">Address line 2 </FormLabel>
                                <TextField
                                    id="address2"
                                    type="text"
                                    name="address2"
                                    placeholder="Enter address line 2"
                                    fullWidth
                                    variant="outlined"
                                    InputProps={{
                                        color: 'secondary',
                                    }}
                                    sx={{ '& .MuiInputBase-input': { fontSize: 'small' } }}
                                    {...getFieldProps('address2')}
                                    error={getFieldError('address2')}
                                    helperText={getFieldError('address2')}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item sm={6} xs={12}>
                            <FormControl fullWidth error={!!getFieldError('city')}>
                                <FormLabel htmlFor="city">City *</FormLabel>
                                <TextField
                                    id="city"
                                    type="text"
                                    name="city"
                                    placeholder="City"
                                    fullWidth
                                    variant="outlined"
                                    InputProps={{
                                        color: 'secondary',
                                    }}
                                    sx={{ '& .MuiInputBase-input': { fontSize: 'small' } }}
                                    {...getFieldProps('city')}
                                    error={getFieldError('city')}
                                    helperText={getFieldError('city')}
                                />
                            </FormControl>
                        </Grid>

                        <Grid container item sm={6} xs={12} columnSpacing={1}>
                            <Grid item sm={6} xs={6}>
                                <FormControl fullWidth error={!!getFieldError('state')}>
                                    <FormLabel htmlFor="state">State *</FormLabel>
                                    <Select
                                        id="state"
                                        name="state"
                                        displayEmpty
                                        variant="outlined"
                                        color="secondary"
                                        {...getFieldProps('state')}
                                        value={getFieldProps('state').value || ''}
                                        onChange={(e) => {
                                            getFieldProps('state').onChange(e);
                                        }}
                                        sx={{
                                            fontSize: 'small',
                                            '& .MuiSelect-icon': {
                                                color: 'secondary.main',
                                            },
                                            '&.Mui-error .MuiSelect-icon': {
                                                color: 'error.main',
                                            },
                                        }}
                                    >
                                        <MenuItem value="">
                                            <em>Select a state</em>
                                        </MenuItem>
                                        {US_STATES.map((state) => (
                                            <MenuItem key={state.code} value={state.code}>
                                                {state.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>{getFieldError('state')}</FormHelperText>
                                </FormControl>
                                {/* <FormControl fullWidth error={!!getFieldError('state')}>
                                    <FormLabel htmlFor="state">State * </FormLabel>
                                    <TextField
                                        id="State"
                                        type="text"
                                        name="state"
                                        placeholder="Enter state"
                                        fullWidth
                                        variant="outlined"
                                        InputProps={{
                                            color: 'secondary',
                                        }}
                                        sx={{ '& .MuiInputBase-input': { fontSize: 'small' } }}
                                        {...getFieldProps('state')}
                                        error={getFieldError('state')}
                                        helperText={getFieldError('state')}
                                    />
                                </FormControl> */}
                            </Grid>
                            <Grid item sm={6} xs={6}>
                                <FormControl fullWidth error={!!getFieldError('postalCode')}>
                                    <FormLabel htmlFor="postalCode">Zip Code * </FormLabel>
                                    <TextField
                                        id="postalCode"
                                        type="text"
                                        name="postalCode"
                                        placeholder="Postal Code"
                                        fullWidth
                                        variant="outlined"
                                        InputProps={{
                                            color: 'secondary',
                                        }}
                                        sx={{ '& .MuiInputBase-input': { fontSize: 'small' } }}
                                        {...getFieldProps('postalCode')}
                                        error={getFieldError('postalCode')}
                                        helperText={getFieldError('postalCode')}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
            </Modal>
        </>
    );
}

export default AddNewPayment;
